import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { FaEdit, FaTrash, FaPlus, FaSignOutAlt, FaSave, FaSearch, FaImage, FaTimes, FaUpload, FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaStore, FaMagic, FaLanguage, FaArrowLeft, FaCloudUploadAlt, FaLink, FaUnlink, FaSpinner } from 'react-icons/fa'
import { SiStripe } from 'react-icons/si'
import { createStripeProduct, updateStripeProduct, deactivateStripePaymentLink, activateStripePaymentLink, createStripePaymentLink } from '../../lib/stripe'
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion'
import StoreItemDetailsPreview from './StoreItemDetailsPreview'
import { useTranslation } from 'react-i18next'
import ServiceHealthBar from '../../components/ServiceHealthBar'

// Image compression utility
const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                const canvas = document.createElement('canvas')
                let width = img.width
                let height = img.height

                if (width > height) {
                    if (width > maxWidth) {
                        height = Math.round((height * maxWidth) / width)
                        width = maxWidth
                    }
                } else {
                    if (height > maxHeight) {
                        width = Math.round((width * maxHeight) / height)
                        height = maxHeight
                    }
                }

                canvas.width = width
                canvas.height = height

                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0, width, height)

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            // Force WebP
                            resolve(new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".webp", { type: 'image/webp' }))
                        } else {
                            reject(new Error('Compression failed'))
                        }
                    },
                    'image/webp',
                    quality
                )
            }
            img.onerror = reject
            img.src = e.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

export default function StoreManagement({ session, onReturn, onLogout }) {
    const { t } = useTranslation()
    const [items, setItems] = useState([])
    const [filteredItems, setFilteredItems] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [editingItem, setEditingItem] = useState(null)
    const [savingAction, setSavingAction] = useState(null)
    const [stripeLoading, setStripeLoading] = useState({})
    const [isTranslating, setIsTranslating] = useState(false)
    const [activeTab, setActiveTab] = useState('es') // 'es' | 'en'
    const [previewScale, setPreviewScale] = useState(1)
    const [showFloatingActions, setShowFloatingActions] = useState(false)
    const previewContainerRef = useRef(null)

    useEffect(() => {
        if (!previewContainerRef.current) return
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const width = entry.contentRect.width
                setPreviewScale(width / 1440)
            }
        })
        observer.observe(previewContainerRef.current)
        return () => observer.disconnect()
    }, [editingItem])

    // Toast Notification State
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' })
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null })

    // Form State
    const initialFormState = {
        plan_number: '',
        title_en: '',
        title_es: '',
        description_en: '',
        description_es: '',
        price: 0,
        is_on_sale: false,
        sale_price: 0,
        is_published: false,
        image_url: '',
        image_gallery: [],
        sq_ft: 0,
        beds: 0,
        baths: 0,
        stories: 1,
        garages: 0,
        width_ft: 0,
        width_in: 0,
        depth_ft: 0,
        depth_in: 0,
        height_ft: 0,
        height_in: 0,
        basement_sq_ft: 0,
        main_floor_sq_ft: 0,
        upper_floor_sq_ft: 0,
        lower_floor_sq_ft: 0,
        main_ceiling_ft: 0,
        upper_ceiling_ft: 0,
        roof_pitch: '',
        roof_load: 0,
        roof_framing: '',
        exterior_wall_finish: '',
        framing: '',
        bedroom_features_en: '',
        bedroom_features_es: '',
        kitchen_features_en: '',
        kitchen_features_es: '',
        included_in_plan_set_en: '',
        included_in_plan_set_es: '',
        style: ''
    }
    const [formData, setFormData] = useState(initialFormState)
    const [imagePreview, setImagePreview] = useState(null)
    const [uploadingImage, setUploadingImage] = useState(false)
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef(null)

    const [uploadingGallery, setUploadingGallery] = useState(false)
    const [galleryDragActive, setGalleryDragActive] = useState(false)
    const galleryInputRef = useRef(null)

    // Fetch items
    const fetchItems = async () => {
        const { data } = await supabase
            .from('store_items')
            .select('*')
            .order('created_at', { ascending: false })
        if (data) {
            setItems(data)
            setFilteredItems(data)
        }
    }

    useEffect(() => {
        if (session) fetchItems()
    }, [session])

    // Handle Scroll for Floating Actions
    useEffect(() => {
        const handleScroll = () => {
            setShowFloatingActions(window.scrollY > 200)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (searchTerm) {
            const q = searchTerm.toLowerCase()
            setFilteredItems(items.filter(item => {
                const title = (item.title_en || '').toLowerCase()
                const plan = (item.plan_number || '').toLowerCase()
                return title.includes(q) || plan.includes(q)
            }))
        } else {
            setFilteredItems(items)
        }
    }, [searchTerm, items])

    const handleCreateNew = () => {
        setEditingItem('new')
        setFormData(initialFormState)
        setImagePreview(null)
    }

    const handleEdit = (item) => {
        setEditingItem(item.id)
        setFormData({
            ...initialFormState,
            ...item
        })
        setImagePreview(item.image_url || null)
    }

    const handleDelete = async (id) => {
        showConfirm(t('admin.store.confirm_delete'), async () => {
            // Deactivate on Stripe first
            const item = items.find(i => i.id === id)
            if (item?.stripe_product_id) {
                try {
                    if (item.stripe_payment_link_id) {
                        await deactivateStripePaymentLink(item.stripe_payment_link_id)
                    }
                    await updateStripeProduct({ product_id: item.stripe_product_id, active: false })
                } catch (stripeErr) {
                    console.error('Stripe deactivation error:', stripeErr)
                }
            }

            const { error } = await supabase.from('store_items').delete().eq('id', id)
            if (!error) {
                fetchItems()
                showToast(t('admin.store.success.delete'), 'success')
            } else {
                showToast(t('admin.store.error.delete'), 'error')
            }
        })
    }

    const handleSave = async (e, publishNow = false) => {
        e.preventDefault()
        if (savingAction) return
        setSavingAction(publishNow ? 'publish' : 'draft')

        try {
            const payload = {
                ...formData,
                is_published: publishNow || formData.is_published,
                updated_at: new Date().toISOString()
            }

            // Remove non-DB fields that might have leaked into formData
            delete payload.stripe_syncing

            let savedItem = null
            let error;
            if (editingItem === 'new') {
                const { data, error: insertError } = await supabase
                    .from('store_items')
                    .insert([payload])
                    .select()
                    .single()

                savedItem = data
                if (data && !payload.is_published) {
                    setEditingItem(data.id)
                    setFormData(data)
                }
                error = insertError
            } else {
                const { data, error: updateError } = await supabase
                    .from('store_items')
                    .update(payload)
                    .eq('id', editingItem)
                    .select()
                    .single()
                savedItem = data
                error = updateError
            }

            if (error) {
                if (error.code === '23505') {
                    showToast(t('admin.store.error.exists'), 'error')
                } else {
                    showToast(t('admin.store.error.save') + ': ' + error.message, 'error')
                }
            } else {
                // ── Stripe sync on publish ──
                if (payload.is_published && savedItem) {
                    try {
                        const productName = savedItem.title_en || `Plan ${savedItem.plan_number}`
                        const productDesc = savedItem.description_en || ''

                        if (!savedItem.stripe_product_id) {
                            // First-time publish → create product + price + payment link
                            showToast(t('admin.store.stripe.syncing', 'Syncing with Stripe...'), 'info')
                            const stripe = await createStripeProduct({
                                name: productName,
                                description: productDesc,
                                price: savedItem.price || 0,
                                images: savedItem.image_url ? [savedItem.image_url] : [],
                                metadata: { plan_number: savedItem.plan_number, supabase_id: savedItem.id },
                            })

                            await supabase.from('store_items').update({
                                stripe_product_id: stripe.product_id,
                                stripe_price_id: stripe.price_id,
                                stripe_payment_link_id: stripe.payment_link_id,
                                stripe_payment_link_url: stripe.payment_link_url,
                            }).eq('id', savedItem.id)

                            showToast(t('admin.store.stripe.created', 'Stripe product & payment link created!'), 'success')
                        } else {
                            // Already on Stripe → update product details
                            await updateStripeProduct({
                                product_id: savedItem.stripe_product_id,
                                name: productName,
                                description: productDesc,
                                images: savedItem.image_url ? [savedItem.image_url] : undefined,
                            })
                            showToast(t('admin.store.stripe.updated', 'Stripe product updated!'), 'success')
                        }
                    } catch (stripeErr) {
                        console.error('Stripe sync error:', stripeErr)
                        showToast(t('admin.store.stripe.error', 'Saved but Stripe sync failed') + ': ' + stripeErr.message, 'error')
                    }
                }

                await fetchItems()
                showToast(t('admin.store.success.save'), 'success')
                if (publishNow || formData.is_published) {
                    setEditingItem(null)
                    setImagePreview(null)
                }
            }
        } catch (err) {
            showToast('Error: ' + err.message, 'error')
        } finally {
            setSavingAction(null)
        }
    }

    const handleImageUpload = async (file) => {
        setUploadingImage(true)
        try {
            const compressedFile = await compressImage(file)
            const fileName = `store-${Date.now()}-${Math.random().toString(36).substring(7)}.webp`

            const { error } = await supabase.storage
                .from('store-images')
                .upload(fileName, compressedFile, {
                    cacheControl: '3600',
                    upsert: false
                })

            if (error) throw error

            const { data: { publicUrl } } = supabase.storage
                .from('store-images')
                .getPublicUrl(fileName)

            setFormData({ ...formData, image_url: publicUrl })
            setImagePreview(publicUrl)
            showToast(t('admin.store.success.upload') || 'Image uploaded!', 'success')
        } catch (error) {
            showToast(t('admin.store.error.upload') + ': ' + error.message, 'error')
        } finally {
            setUploadingImage(false)
        }
    }

    const handleGalleryUpload = async (files) => {
        const currentGallery = formData.image_gallery || []
        if (currentGallery.length + files.length > 5) {
            showToast(t('admin.store.error.gallery_full') || 'You can only upload up to 5 images in the gallery', 'error')
            return
        }

        setUploadingGallery(true)
        try {
            const newUrls = []
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                const compressedFile = await compressImage(file)
                const fileName = `store-gallery-${Date.now()}-${Math.random().toString(36).substring(7)}.webp`

                const { error } = await supabase.storage
                    .from('store-images')
                    .upload(fileName, compressedFile, { cacheControl: '3600', upsert: false })

                if (error) throw error

                const { data: { publicUrl } } = supabase.storage
                    .from('store-images')
                    .getPublicUrl(fileName)

                newUrls.push(publicUrl)
            }

            setFormData(prev => ({ ...prev, image_gallery: [...(prev.image_gallery || []), ...newUrls] }))
            showToast(t('admin.store.success.upload_gallery') || 'Gallery images uploaded!', 'success')
        } catch (error) {
            showToast(t('admin.store.error.upload') + ': ' + error.message, 'error')
        } finally {
            setUploadingGallery(false)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageUpload(e.dataTransfer.files[0])
        }
    }

    const handleGalleryDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setGalleryDragActive(false)
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleGalleryUpload(e.dataTransfer.files)
        }
    }

    const handleTogglePaymentLink = async (item, activate) => {
        setStripeLoading(prev => ({ ...prev, [item.id]: true }))
        try {
            if (activate) {
                if (item.stripe_payment_link_id) {
                    // Re-enable an existing deactivated link
                    const res = await activateStripePaymentLink(item.stripe_payment_link_id)
                    await supabase.from('store_items').update({
                        stripe_payment_link_url: res.url,
                    }).eq('id', item.id)
                } else if (item.stripe_price_id) {
                    // No link exists yet, create one
                    const link = await createStripePaymentLink(item.stripe_price_id)
                    await supabase.from('store_items').update({
                        stripe_payment_link_id: link.payment_link_id,
                        stripe_payment_link_url: link.payment_link_url,
                    }).eq('id', item.id)
                }
                showToast(t('admin.store.stripe.link_activated', 'Payment link activated'), 'success')
            } else {
                if (item.stripe_payment_link_id) {
                    await deactivateStripePaymentLink(item.stripe_payment_link_id)
                    // Null the URL so UI reflects deactivated state
                    await supabase.from('store_items').update({
                        stripe_payment_link_url: null,
                    }).eq('id', item.id)
                }
                showToast(t('admin.store.stripe.link_deactivated', 'Payment link deactivated'), 'success')
            }
            await fetchItems()
        } catch (err) {
            console.error('Payment link toggle error:', err)
            showToast(t('admin.store.stripe.error', 'Stripe error') + ': ' + err.message, 'error')
        } finally {
            setStripeLoading(prev => ({ ...prev, [item.id]: false }))
        }
    }

    // Modal Helpers
    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type })
        setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 4000)
    }

    const showConfirm = (message, onConfirm) => {
        setConfirmDialog({ show: true, message, onConfirm })
    }

    const handleConfirm = () => {
        if (confirmDialog.onConfirm) confirmDialog.onConfirm()
        setConfirmDialog({ show: false, message: '', onConfirm: null })
    }

    const handleCancelConfirm = () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null })
    }


    const translateFields = async (fields, sourceLang, targetLang) => {
        if (!fields || Object.keys(fields).length === 0) return {}

        const { data, error } = await supabase.functions.invoke('translate-post', {
            body: {
                fields,
                sourceLang,
                targetLang
            }
        })

        if (error) {
            console.error('Translation function error:', error)
            throw new Error(error.message || 'Translation failed')
        }

        if (!data || (!data.translatedFields && !data)) {
            throw new Error('Translation returned no data')
        }

        // Defensively unwrap in case the LLM returned it nested (e.g., {translatedFields: {translatedFields: {...}}})
        let result = data.translatedFields || data
        if (result && result.translatedFields) {
            result = result.translatedFields
        }

        return result
    }

    const handleAutoTranslate = async (e) => {
        e.preventDefault()

        const sourceLang = activeTab
        const targetLang = activeTab === 'en' ? 'es' : 'en'

        const fieldsToTranslate = {}
        const titleSource = formData[`title_${sourceLang}`]
        const descSource = formData[`description_${sourceLang}`]

        // These fields are shared but intended to be translated (e.g., CSV lists)
        const bedFeatures = formData[`bedroom_features_${sourceLang}`]
        const kitchenFeatures = formData[`kitchen_features_${sourceLang}`]
        const includedInPlan = formData[`included_in_plan_set_${sourceLang}`]

        if (titleSource) fieldsToTranslate.title = titleSource
        if (descSource) fieldsToTranslate.content = descSource

        // Map additional fields to non-conflicting names for the AI
        if (bedFeatures) fieldsToTranslate.bedroom_features = bedFeatures
        if (kitchenFeatures) fieldsToTranslate.kitchen_features = kitchenFeatures
        if (includedInPlan) fieldsToTranslate.included_in_plan = includedInPlan

        if (Object.keys(fieldsToTranslate).length === 0) {
            showToast(t('admin.language.translate_empty') || 'Please fill out title/description first.', 'info')
            return
        }

        setIsTranslating(true)
        try {
            console.log("Sending to translate:", { fieldsToTranslate, sourceLang, targetLang })
            const translatedFields = await translateFields(fieldsToTranslate, sourceLang, targetLang)
            console.log("Received translated fields:", translatedFields)

            setFormData(prev => {
                const updated = {
                    ...prev,
                    [`title_${targetLang}`]: translatedFields.title || prev[`title_${targetLang}`],
                    [`description_${targetLang}`]: translatedFields.content || translatedFields.description || prev[`description_${targetLang}`]
                }

                // Map translated feature fields to the appropriate language target field
                if (translatedFields.bedroom_features) updated[`bedroom_features_${targetLang}`] = translatedFields.bedroom_features
                if (translatedFields.kitchen_features) updated[`kitchen_features_${targetLang}`] = translatedFields.kitchen_features
                if (translatedFields.included_in_plan) updated[`included_in_plan_set_${targetLang}`] = translatedFields.included_in_plan

                console.log("Setting form data to:", updated)
                return updated
            })
            setActiveTab(targetLang)

            showToast(t('admin.language.translate_success') || 'Translation complete', 'success')
        } catch (error) {
            console.error(error)
            showToast(t('admin.language.translate_error') + ': ' + error.message, 'error')
        } finally {
            setIsTranslating(false)
        }
    }
    // Render Editor
    if (editingItem) {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="overflow-x-clip min-h-screen bg-cloud">
                <header className="sticky top-20 z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                        <button
                            onClick={() => setEditingItem(null)}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0"
                            title={t('common.return')}
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-xl font-bold text-primary flex-1 min-w-0 truncate">
                            {editingItem === 'new' ? t('admin.store.new_item') : t('admin.store.edit_item')}
                        </h1>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={(e) => handleSave(e, false)}
                                disabled={savingAction === 'draft'}
                                className="flex gap-2 items-center px-4 py-2 text-sm font-medium rounded-lg border transition-colors border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-50"
                            >
                                {savingAction === 'draft' ? (
                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : <FaSave />}
                                {t('admin.store.save_draft')}
                            </button>
                            <button
                                onClick={(e) => handleSave(e, true)}
                                disabled={savingAction === 'publish'}
                                className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white rounded-lg shadow-sm transition-colors bg-primary hover:bg-primary/90 disabled:opacity-50"
                            >
                                {savingAction === 'publish' ? (
                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : <FaCloudUploadAlt />}
                                {t('admin.store.publish')}
                            </button>
                        </div>
                    </div>
                    <div className="px-4 pb-2 mx-auto max-w-7xl">
                        <ServiceHealthBar />
                    </div>
                </header>

                <div className="max-w-[1600px] mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
                        {/* Left: Form */}
                        <div className="space-y-6">
                            <form className="p-6 space-y-8 bg-white rounded-xl border border-gray-200 shadow-sm md:p-8">

                                {/* Basic Info */}
                                <div className="space-y-4">
                                    <div className="flex flex-col gap-4 justify-between items-start pb-4 border-b sm:flex-row sm:items-center">
                                        <h2 className="text-lg font-semibold">{t('admin.store.basic_info')}</h2>

                                        <div className="flex gap-4 items-center">
                                            {/* Language Tabs */}
                                            <div className="flex p-1 rounded-lg border border-gray-200 bg-gray-100/50">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setActiveTab('es'); }}
                                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'es'
                                                        ? 'bg-white text-ink shadow-sm ring-1 ring-gray-200'
                                                        : 'text-ash hover:text-ink hover:bg-gray-100/50'
                                                        }`}
                                                >
                                                    <FaLanguage className="inline mr-2" />
                                                    {t('admin.language.es')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setActiveTab('en'); }}
                                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'en'
                                                        ? 'bg-white text-ink shadow-sm ring-1 ring-gray-200'
                                                        : 'text-ash hover:text-ink hover:bg-gray-100/50'
                                                        }`}
                                                >
                                                    <FaLanguage className="inline mr-2" />
                                                    {t('admin.language.en')}
                                                </button>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={handleAutoTranslate}
                                                disabled={isTranslating}
                                                className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 rounded-lg transition-all duration-300 group hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FaMagic className={`${isTranslating ? 'animate-spin' : 'group-hover:rotate-12'} transition-transform duration-300`} />
                                                {isTranslating
                                                    ? t('admin.store.translating')
                                                    : activeTab === 'en'
                                                        ? t('admin.language.translate_to_es')
                                                        : t('admin.language.translate_to_en')
                                                }
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium">{t('admin.store.plan_number')}</label>
                                            <input
                                                type="text"
                                                value={formData.plan_number}
                                                onChange={e => {
                                                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '').replace(/[_-]{2,}/g, '_').replace(/^_+|_+$/g, '')
                                                    setFormData(p => ({ ...p, plan_number: v }))
                                                }}
                                                onBlur={() => {
                                                    if (!formData.plan_number) {
                                                        const fromTitle = formData.title_en?.toUpperCase().replace(/[^A-Z0-9\s]/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '')
                                                        if (fromTitle) setFormData(p => ({ ...p, plan_number: fromTitle }))
                                                    }
                                                }}
                                                className="px-4 py-2 w-full rounded-lg border focus:ring-2 focus:ring-primary font-mono text-sm"
                                                placeholder="PLAN-123-A"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium">{t('admin.store.price_label')}</label>
                                            <input type="number" step="0.01" value={formData.price} onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} className="px-4 py-2 w-full rounded-lg border focus:ring-2 focus:ring-primary" required />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-sm font-medium">Style</label>
                                        <input type="text" value={formData.style || ''} onChange={e => setFormData({ ...formData, style: e.target.value })} placeholder="e.g. Modern, Craftsman, Farmhouse" className="px-4 py-2 w-full rounded-lg border focus:ring-2 focus:ring-primary" />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-sm font-medium">
                                            {activeTab === 'en' ? t('admin.store.title_en') : t('admin.store.title_es')}
                                        </label>
                                        <input
                                            type="text"
                                            value={activeTab === 'en' ? formData.title_en : formData.title_es}
                                            onChange={e => setFormData({ ...formData, [activeTab === 'en' ? 'title_en' : 'title_es']: e.target.value })}
                                            className="px-4 py-2 w-full rounded-lg border focus:ring-2 focus:ring-primary"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-sm font-medium">
                                            {activeTab === 'en' ? t('admin.store.description_en') : t('admin.store.description_es')}
                                        </label>
                                        <textarea
                                            value={activeTab === 'en' ? formData.description_en : formData.description_es}
                                            onChange={e => setFormData({ ...formData, [activeTab === 'en' ? 'description_en' : 'description_es']: e.target.value })}
                                            className="px-4 py-2 w-full rounded-lg border focus:ring-2 focus:ring-primary"
                                            rows="3"
                                        />
                                    </div>
                                </div>

                                {/* Image */}
                                <div>
                                    <h2 className="pb-2 mb-4 text-lg font-semibold border-b">{t('admin.store.main_image')}</h2>
                                    {!imagePreview ? (
                                        <div
                                            onDragEnter={() => setDragActive(true)}
                                            onDragLeave={() => setDragActive(false)}
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${dragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary/50'}`}
                                        >
                                            <FaUpload className="mx-auto mb-2 text-3xl text-gray-400" />
                                            <p className="font-medium">{uploadingImage ? t('admin.store.compressing') : t('admin.store.drop_image')}</p>
                                            <p className="text-sm text-gray-500">{t('admin.store.image_hint')}</p>
                                            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) handleImageUpload(e.target.files[0]) }} className="hidden" disabled={uploadingImage} />
                                        </div>
                                    ) : (
                                        <div className="inline-block overflow-hidden relative rounded-lg border border-gray-200">
                                            <img src={imagePreview} className="object-cover h-48" alt="Preview" />
                                            <button onClick={() => { setFormData({ ...formData, image_url: '' }); setImagePreview(null); }} className="absolute top-2 right-2 p-2 text-white bg-red-600 rounded-full transition hover:bg-red-700">
                                                <FaTimes />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Gallery */}
                                <div>
                                    <h2 className="pb-2 mb-4 text-lg font-semibold border-b">{t('admin.store.gallery_images')}</h2>

                                    <div className="flex flex-wrap gap-4 mb-4">
                                        {(formData.image_gallery || []).map((url, idx) => (
                                            <div key={idx} className="inline-block overflow-hidden relative w-32 h-32 rounded-lg border border-gray-200">
                                                <img src={url} className="object-cover w-full h-full" alt="Gallery preview" />
                                                <button onClick={() => {
                                                    const newGallery = formData.image_gallery.filter((_, i) => i !== idx)
                                                    setFormData({ ...formData, image_gallery: newGallery })
                                                }} className="absolute top-1 right-1 bg-red-600 text-white p-1.5 text-xs rounded-full hover:bg-red-700 transition">
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {(formData.image_gallery || []).length < 5 && (
                                        <div
                                            onDragEnter={() => setGalleryDragActive(true)}
                                            onDragLeave={() => setGalleryDragActive(false)}
                                            onDragOver={e => e.preventDefault()}
                                            onDrop={handleGalleryDrop}
                                            onClick={() => galleryInputRef.current?.click()}
                                            className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${galleryDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary/50'}`}
                                        >
                                            <FaUpload className="mx-auto mb-2 text-2xl text-gray-400" />
                                            <p className="text-sm font-medium">{uploadingGallery ? t('admin.store.uploading_gallery') : t('admin.store.gallery_drop')}</p>
                                            <input ref={galleryInputRef} type="file" multiple accept="image/*" onChange={(e) => { if (e.target.files && e.target.files.length > 0) handleGalleryUpload(e.target.files) }} className="hidden" disabled={uploadingGallery} />
                                        </div>
                                    )}
                                </div>

                                {/* Key Specs */}
                                <div className="space-y-4">
                                    <h2 className="pb-2 text-lg font-semibold border-b">{t('admin.store.key_specs')}</h2>
                                    <div className="grid grid-cols-5 gap-4">
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.sqft')}</label>
                                            <input type="number" step="any" value={formData.sq_ft} onChange={e => setFormData({ ...formData, sq_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.beds')}</label>
                                            <input type="number" value={formData.beds} onChange={e => setFormData({ ...formData, beds: parseInt(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.baths')}</label>
                                            <input type="number" step="0.5" value={formData.baths} onChange={e => setFormData({ ...formData, baths: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.stories')}</label>
                                            <input type="number" value={formData.stories} onChange={e => setFormData({ ...formData, stories: parseInt(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.garages')}</label>
                                            <input type="number" value={formData.garages} onChange={e => setFormData({ ...formData, garages: parseInt(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                    </div>
                                </div>

                                {/* Dimensions & Areas */}
                                <div className="space-y-4">
                                    <h2 className="pb-2 text-lg font-semibold border-b">{t('admin.store.dimensions')}</h2>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.width_ft')}</label>
                                                <input type="number" step="any" value={formData.width_ft} onChange={e => setFormData({ ...formData, width_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.width_in')}</label>
                                                <input type="number" step="any" value={formData.width_in} onChange={e => setFormData({ ...formData, width_in: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.depth_ft')}</label>
                                                <input type="number" step="any" value={formData.depth_ft} onChange={e => setFormData({ ...formData, depth_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.depth_in')}</label>
                                                <input type="number" step="any" value={formData.depth_in} onChange={e => setFormData({ ...formData, depth_in: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.height_ft')}</label>
                                                <input type="number" step="any" value={formData.height_ft} onChange={e => setFormData({ ...formData, height_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                            <div className="flex-1">
                                                <label className="block mb-1 text-xs font-medium">{t('admin.store.height_in')}</label>
                                                <input type="number" step="any" value={formData.height_in} onChange={e => setFormData({ ...formData, height_in: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-4 gap-4">
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.basement')}</label>
                                            <input type="number" step="any" value={formData.basement_sq_ft} onChange={e => setFormData({ ...formData, basement_sq_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.main_floor')}</label>
                                            <input type="number" step="any" value={formData.main_floor_sq_ft} onChange={e => setFormData({ ...formData, main_floor_sq_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.upper_floor')}</label>
                                            <input type="number" step="any" value={formData.upper_floor_sq_ft} onChange={e => setFormData({ ...formData, upper_floor_sq_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.lower_floor')}</label>
                                            <input type="number" step="any" value={formData.lower_floor_sq_ft} onChange={e => setFormData({ ...formData, lower_floor_sq_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Features */}
                                <div className="space-y-4">
                                    <h2 className="pb-2 text-lg font-semibold border-b">{t('admin.store.full_specs')}</h2>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.main_ceiling')}</label>
                                            <input type="number" step="any" value={formData.main_ceiling_ft} onChange={e => setFormData({ ...formData, main_ceiling_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.upper_ceiling')}</label>
                                            <input type="number" step="any" value={formData.upper_ceiling_ft} onChange={e => setFormData({ ...formData, upper_ceiling_ft: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.roof_pitch')}</label>
                                            <input type="text" placeholder="e.g. 9/12" value={formData.roof_pitch} onChange={e => setFormData({ ...formData, roof_pitch: e.target.value })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.roof_load')}</label>
                                            <input type="number" step="any" value={formData.roof_load} onChange={e => setFormData({ ...formData, roof_load: parseFloat(e.target.value) || 0 })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.roof_framing')}</label>
                                            <input type="text" placeholder="e.g. Truss/Stick" value={formData.roof_framing} onChange={e => setFormData({ ...formData, roof_framing: e.target.value })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.exterior_wall')}</label>
                                            <input type="text" value={formData.exterior_wall_finish} onChange={e => setFormData({ ...formData, exterior_wall_finish: e.target.value })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-xs font-medium">{t('admin.store.framing')}</label>
                                            <input type="text" placeholder="e.g. 2x6" value={formData.framing} onChange={e => setFormData({ ...formData, framing: e.target.value })} className="px-3 py-2 w-full rounded-lg border" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium">{t('admin.store.bed_features')}</label>
                                            <textarea placeholder={t('admin.store.features_hint')} value={activeTab === 'en' ? formData.bedroom_features_en : formData.bedroom_features_es} onChange={e => setFormData({ ...formData, [activeTab === 'en' ? 'bedroom_features_en' : 'bedroom_features_es']: e.target.value })} className="px-3 py-2 w-full rounded-lg border" rows="2" />
                                        </div>
                                        <div>
                                            <label className="block mb-1 text-sm font-medium">{t('admin.store.kitchen_features')}</label>
                                            <textarea placeholder={t('admin.store.features_hint')} value={activeTab === 'en' ? formData.kitchen_features_en : formData.kitchen_features_es} onChange={e => setFormData({ ...formData, [activeTab === 'en' ? 'kitchen_features_en' : 'kitchen_features_es']: e.target.value })} className="px-3 py-2 w-full rounded-lg border" rows="2" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block mb-1 text-sm font-medium">{t('admin.store.included')}</label>
                                            <textarea placeholder={t('admin.store.included_hint')} value={activeTab === 'en' ? formData.included_in_plan_set_en : formData.included_in_plan_set_es} onChange={e => setFormData({ ...formData, [activeTab === 'en' ? 'included_in_plan_set_en' : 'included_in_plan_set_es']: e.target.value })} className="px-3 py-2 w-full rounded-lg border" rows="3" />
                                        </div>
                                    </div>
                                </div>

                            </form>
                        </div>

                        {/* Right: Preview */}
                        <div className="mt-6 xl:sticky xl:top-24 xl:self-start xl:mt-0">
                            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-lg flex flex-col h-[calc(100vh-8rem)]">
                                <div className="flex z-10 justify-between items-center px-4 py-3 bg-gray-50 border-b shadow-sm shrink-0">
                                    <div className="flex gap-3 items-center">
                                        <h3 className="text-lg font-bold text-primary">{t('admin.store.live_preview')}</h3>
                                        <span className={`px-2 py-1 text-xs font-bold rounded ${activeTab === 'en' ? 'bg-primary/10 text-primary' : 'bg-blue-100 text-blue-700'}`}>
                                            {activeTab === 'en' ? t('admin.store.english_view') : t('admin.store.spanish_view')}
                                        </span>
                                    </div>
                                </div>
                                <div className="overflow-hidden relative flex-1 p-0 m-0 bg-cloud" ref={previewContainerRef}>
                                    <div
                                        className="absolute top-0 left-0 w-[1440px] origin-top-left overflow-y-auto overflow-x-hidden custom-scrollbar"
                                        style={{
                                            transform: `scale(${previewScale})`,
                                            height: `${100 / previewScale}%`
                                        }}
                                    >
                                        <div className="pb-24">
                                            <StoreItemDetailsPreview item={formData} previewLang={activeTab} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toast Notification */}
                <AnimatePresence>
                    {toast.show && (
                        <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed bottom-8 right-8 z-[70]">
                            <div className={`px-6 py-4 rounded-lg shadow-xl text-white ${toast.type === 'error' ? 'bg-red-600' : toast.type === 'success' ? 'bg-green-600' : 'bg-primary'}`}>
                                {toast.message}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Floating Actions (Emoji only, Scroll-dependent) */}
                <AnimatePresence>
                    {showFloatingActions && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-10 right-10 z-[60] flex flex-col gap-3"
                        >
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                    setEditingItem(null)
                                    window.scrollTo({ top: 0, behavior: 'smooth' })
                                }}
                                className="p-3 text-lg bg-white rounded-full border border-gray-100 shadow-2xl transition-all text-ash hover:text-ink"
                                title={t('common.return')}
                            >
                                ⬅️
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleSave(e, false)}
                                className="p-3 text-lg bg-white rounded-full border shadow-2xl transition-all text-primary border-primary/20"
                                title={t('admin.store.save_draft')}
                            >
                                {savingAction === 'draft' ? '⏳' : '💾'}
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => handleSave(e, true)}
                                className="p-4 text-xl text-white rounded-full border shadow-2xl transition-all bg-primary border-primary/20"
                                title={t('admin.store.publish')}
                            >
                                {savingAction === 'publish' ? '⏳' : '🚀'}
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        )
    }

    // Dashboard View
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="overflow-x-clip min-h-screen bg-cloud">
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button onClick={onReturn} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all text-ash shrink-0" title={t('common.return')}>
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-xl font-bold text-primary flex-1">{t('admin.store.title')}</h1>
                    <button onClick={handleCreateNew} className="px-3 py-1.5 text-sm bg-primary text-ink hover:bg-primary/90 transition-all flex items-center gap-2 font-semibold rounded-lg shrink-0">
                        <FaPlus /> {t('admin.store.new_plan')}
                    </button>
                    <button onClick={onLogout} className="p-2 rounded-lg border border-gray-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all text-ash shrink-0" title={t('auth.signOut')}>
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="max-w-7xl mx-auto px-4 pb-2">
                    <ServiceHealthBar />
                </div>
            </header>

            <div className="px-4 py-6 mx-auto max-w-7xl">
                <div className="relative mb-6">
                    <FaSearch className="absolute left-4 top-1/2 text-gray-400 -translate-y-1/2" />
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t('admin.store.search_placeholder')} className="py-3 pr-4 pl-12 w-full rounded-lg border border-gray-200" />
                </div>

                <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                    <table className="w-full min-w-[500px]">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.store.table.item')}</th>
                                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.store.table.price')}</th>
                                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">{t('admin.store.table.status')}</th>
                                <th className="px-6 py-3 text-xs font-medium text-center text-gray-500 uppercase">Stripe</th>
                                <th className="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase">{t('admin.store.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredItems.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="flex gap-3 items-center px-6 py-4">
                                        {item.image_url && <img src={item.image_url} className="object-cover w-12 h-12 rounded" alt="Plan" />}
                                        <div>
                                            <p className="font-medium text-gray-900">{item.title_en || t('admin.store.untitled')}</p>
                                            <p className="text-sm text-gray-500">{item.plan_number}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-gray-900">${item.price.toFixed(2)}</td>
                                    <td className="px-6 py-4">
                                        <span className={`text-sm ${item.is_published ? 'text-green-600' : 'text-amber-600'}`}>
                                            {item.is_published ? t('admin.store.status.published') : t('admin.store.status.draft')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {item.stripe_product_id ? (
                                            <div className="flex flex-col gap-1 items-center">
                                                <SiStripe className="text-[#635bff] text-lg" title="Synced with Stripe" />
                                                {item.stripe_payment_link_url ? (
                                                    <div className="flex gap-1 items-center">
                                                        <a href={item.stripe_payment_link_url} target="_blank" rel="noopener noreferrer" className="flex gap-1 items-center text-xs text-green-600 hover:underline">
                                                            <FaLink className="text-[10px]" /> Link
                                                        </a>
                                                        <button
                                                            onClick={() => handleTogglePaymentLink(item, false)}
                                                            disabled={stripeLoading[item.id]}
                                                            className="p-1 ml-1 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                                                            title={t('admin.store.stripe.deactivate_link', 'Deactivate link')}
                                                        >
                                                            {stripeLoading[item.id] ? <FaSpinner className="text-xs animate-spin" /> : <FaUnlink className="text-xs" />}
                                                        </button>
                                                    </div>
                                                ) : item.stripe_payment_link_id ? (
                                                    <button
                                                        onClick={() => handleTogglePaymentLink(item, true)}
                                                        disabled={stripeLoading[item.id]}
                                                        className="flex gap-1 items-center text-xs text-amber-600 hover:text-amber-800 disabled:opacity-50"
                                                        title={t('admin.store.stripe.reactivate_link', 'Re-enable link')}
                                                    >
                                                        {stripeLoading[item.id] ? <FaSpinner className="text-xs animate-spin" /> : <FaLink className="text-xs" />}
                                                        <span>{t('admin.store.stripe.reenable', 'Re-enable')}</span>
                                                    </button>
                                                ) : item.stripe_price_id ? (
                                                    <button
                                                        onClick={() => handleTogglePaymentLink(item, true)}
                                                        disabled={stripeLoading[item.id]}
                                                        className="flex gap-1 items-center text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                        title={t('admin.store.stripe.activate_link', 'Create payment link')}
                                                    >
                                                        {stripeLoading[item.id] ? <FaSpinner className="text-xs animate-spin" /> : <FaLink className="text-xs" />}
                                                        <span>{t('admin.store.stripe.enable', 'Enable')}</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex gap-2 justify-end">
                                            <button onClick={() => handleEdit(item)} className="p-2 rounded-lg border hover:border-primary hover:text-primary"><FaEdit /></button>
                                            <button onClick={() => handleDelete(item.id)} className="p-2 rounded-lg border hover:border-red-600 hover:text-red-600"><FaTrash /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredItems.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 font-medium text-center text-gray-500">{t('admin.store.no_items')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>


            <AnimatePresence>
                {confirmDialog.show && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex fixed inset-0 z-50 justify-center items-center bg-black/60">
                        <div className="p-6 w-full max-w-sm bg-white rounded-lg">
                            <h3 className="mb-2 text-lg font-bold">{t('admin.confirmAction')}</h3>
                            <p className="mb-6 text-gray-600">{confirmDialog.message}</p>
                            <div className="flex gap-2 justify-end">
                                <button onClick={handleCancelConfirm} className="px-4 py-2 rounded-lg border">{t('common.cancel')}</button>
                                <button onClick={handleConfirm} className="px-4 py-2 text-white bg-red-600 rounded-lg">{t('common.confirm')}</button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toast Notification */}
            <AnimatePresence>
                {toast.show && (
                    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} className="fixed bottom-8 right-8 z-[70]">
                        <div className={`px-6 py-4 rounded-lg shadow-xl text-white ${toast.type === 'error' ? 'bg-red-600' : toast.type === 'success' ? 'bg-green-600' : 'bg-primary'}`}>
                            {toast.message}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}
